import { formatarIdentificacaoObra } from "@/shared/utils/formatar-identificacao-obra";
import {
  buildTimeline,
  extendPeriodo,
  positionInterval,
  snapToYears,
} from "@/modules/carga-trabalho";
import type {
  CargaTrabalhoAlocacao,
  CargaTrabalhoPeriodo,
  CargaTrabalhoProfissionaisData,
  CargaTrabalhoProfissional,
  GanttIndicators,
  IsoDate,
  Timeline,
} from "@/modules/carga-trabalho";
import type { FornecedorDisponivel } from "@/modules/recursos";
import {
  buildProfissionalRows,
  obraLabel,
  rangeLine,
} from "@/shared/components/gantt-carga/gantt-rows";
import type { GanttRowModel } from "@/shared/components/gantt-carga/gantt-rows";

/** Cancelada segue o indicador da tela (bug 3413); a transferida é desenhada à parte. */
function indicadoresDaRevisao(canceladaVisivel: boolean): GanttIndicators {
  return {
    planejamento: true,
    obra: true,
    fechamento: true,
    cancelada: canceladaVisivel,
    indisponibilidade: false,
  };
}

const TRANSFERIDA_KEY_SUFFIX = "-transferida";

export interface RevisaoFornecedorInput {
  /** Fornecedor em conflito, como veio da Carga de Trabalho. */
  fornecedor: CargaTrabalhoProfissional;
  /** Alocação que passará ao substituto. */
  alocacao: CargaTrabalhoAlocacao;
  substituto: FornecedorDisponivel;
  /** Carga de trabalho carregada na tela: fonte das alocações do substituto. */
  carga: CargaTrabalhoProfissionaisData | null;
  periodo: CargaTrabalhoPeriodo;
  today: IsoDate;
  canceladaVisivel: boolean;
}

/** Alocações do substituto: as da carga carregada, quando ele aparece nela; senão, nenhuma. */
function substitutoNaCarga({
  substituto,
  carga,
}: RevisaoFornecedorInput): CargaTrabalhoProfissional {
  const encontrado = carga?.profissionais.find((item) => item.id === substituto.id);
  if (encontrado) return { ...encontrado, conflitosIgnorados: [] };
  return {
    id: substituto.id,
    nome: substituto.nome,
    time: null,
    alocacoes: [],
    indisponibilidades: [],
  };
}

/**
 * Gantt da etapa Revisão (HU 2041): uma linha para o fornecedor substituído e outra para o
 * substituto, com a obra transferida tracejada e o período da alocação em destaque nas duas.
 */
export function buildRevisaoFornecedorGantt(input: RevisaoFornecedorInput): {
  rows: GanttRowModel[];
  timeline: Timeline;
} {
  const { fornecedor, alocacao, periodo, today, canceladaVisivel } = input;
  const substituto = substitutoNaCarga(input);
  const transferidaId = `${alocacao.id}${TRANSFERIDA_KEY_SUFFIX}`;

  const data: CargaTrabalhoProfissionaisData = {
    periodo,
    profissionais: [
      {
        ...fornecedor,
        // A alocação inteira passa ao substituto: some da linha do fornecedor substituído.
        alocacoes: fornecedor.alocacoes.filter((item) => item.id !== alocacao.id),
        conflitosIgnorados: [],
      },
      {
        ...substituto,
        alocacoes: [...substituto.alocacoes, { ...alocacao, id: transferidaId }],
      },
    ],
  };

  const timeline = buildTimeline(snapToYears(extendPeriodo(periodo, data)), alocacao.inicio);
  const highlight = positionInterval(alocacao, timeline);
  const rows = buildProfissionalRows(
    data,
    timeline,
    indicadoresDaRevisao(canceladaVisivel),
    today,
  ).map((row, index) => {
    const total = data.profissionais[index]?.alocacoes.length ?? 0;
    // Na linha do substituto, a obra transferida ainda não conta como alocação dele.
    const alocacoes = index === 1 ? total - 1 : total;
    return {
      ...row,
      meta: `${alocacoes} ${alocacoes === 1 ? "Alocação" : "Alocações"}`,
      conflicts: 0,
      conflictTooltip: "",
      conflitos: [],
      items: row.items.map((item) => {
        if (item.key !== transferidaId) return item;
        const position = positionInterval(alocacao, timeline);
        return {
          ...item,
          left: position?.left ?? item.left,
          width: position?.width ?? item.width,
          label: obraLabel(alocacao.obra),
          past: false,
          transferida: true,
          segments: [{ kind: "transferida" as const, left: 0, width: 100 }],
          tooltip: [
            `Obra ${formatarIdentificacaoObra(alocacao.obra)}`,
            "Situação: Obra Transferida",
            rangeLine("Alocação", alocacao),
          ].join("\n"),
        };
      }),
      bands: highlight
        ? [
            {
              key: `${row.key}-destaque`,
              left: highlight.left,
              width: highlight.width,
              tooltip: rangeLine("Período da substituição", alocacao),
              highlight: true,
            },
          ]
        : [],
    };
  });

  return { rows, timeline };
}
